import { useState, useRef } from "react";
import { Trash2, Plus, ChevronDown, GripVertical, Users, UserPlus, Eye, EyeOff } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { CATEGORIES, TAGLINES } from "../data/catalog";
import { PRICE_MODES, rowTotal, monthlyRowTotal, computeTotals } from "../lib/pricing";
import { eur } from "../lib/format";
import { normalizeRichRuns } from "../lib/richText";

const Section = ({ n, title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="fb-section" data-testid={`section-${n}`}>
      <button
        type="button"
        className="fb-section-head"
        onClick={() => setOpen((o) => !o)}
        data-testid={`section-toggle-${n}`}
      >
        <span className="fb-section-index">{n}</span>
        <span className="fb-section-title">{title}</span>
        <ChevronDown
          size={16}
          className={`fb-chevron ${open ? "open" : ""}`}
        />
      </button>
      {open && <div className="fb-section-body">{children}</div>}
    </div>
  );
};

const Field = ({ label, children }) => (
  <div className="fb-field">
    <Label className="fb-label">{label}</Label>
    {children}
  </div>
);

export const FormBuilder = ({
  quote,
  set,
  setClient,
  items,
  catalog = [],
  onSaveCatalogItem,
  onSaveClient,
  onPickClient,
}) => {
  const totals = computeTotals(quote);
  const dragIndex = useRef(null);
  const [dragOver, setDragOver] = useState(null);
  const suffix =
    (PRICE_MODES.find((m) => m.key === quote.priceMode) || PRICE_MODES[0])
      .suffix;

  const groupedCatalog = catalog.reduce((groups, item) => {
    const category = item.category || "Altro";
    if (!groups[category]) groups[category] = [];
    groups[category].push(item);
    return groups;
  }, {});

  const categoryDot = (label) =>
    CATEGORIES.find((c) => c.label === label)?.dot || "#777";

  const addFromCatalog = (value) => {
    const preset = catalog.find((item) => item.id === value);
    if (!preset) return;
    const recurring =
      ["Integrated Software", "In-Store Hardware"].includes(preset.category) ||
      preset.name === "Campagne Meta Ads";
    items.add({
      catalogId: preset.id,
      category: preset.category,
      name: preset.name,
      description: preset.description,
      descriptionRich: normalizeRichRuns(
        preset.description || "",
        preset.descriptionRich || preset.richDescription || preset.descriptionRuns || [],
      ),
      recurring,
    });
  };

  const saveFreeItem = async (item) => {
    if (!onSaveCatalogItem) return;
    const saved = await onSaveCatalogItem(item);
    if (saved?.id) items.update(item.id, "catalogId", saved.id);
  };

  const roundTo = (step) => {
    const base = totals.computedFinal || 0;
    const rounded = Math.round(base / step) * step;
    set("roundTotal", String(rounded));
  };

  return (
    <div className="fb-root" data-testid="form-builder">
      {/* 1. Dati preventivo */}
      <Section n="1" title="Dati preventivo">
        <div className="fb-grid-2">
          <Field label="N° Preventivo">
            <Input
              data-testid="input-number"
              value={quote.number}
              placeholder="Automatico al primo salvataggio"
              readOnly
            />
          </Field>
          <Field label="Data">
            <Input
              type="date"
              data-testid="input-date"
              value={quote.date}
              onChange={(e) => set("date", e.target.value)}
            />
          </Field>
        </div>
        <Field label="Titolo progetto / cliente">
          <Input
            data-testid="input-title"
            placeholder="es. NOVATE"
            value={quote.projectTitle}
            onChange={(e) => set("projectTitle", e.target.value)}
          />
        </Field>
        <Field label="Tagline introduttiva">
          <Select onValueChange={(v) => set("tagline", v)}>
            <SelectTrigger
              className="fb-tagline-select"
              data-testid="select-tagline"
            >
              <SelectValue placeholder="Scegli una tagline predefinita…" />
            </SelectTrigger>
            <SelectContent>
              {TAGLINES.map((t, i) => (
                <SelectItem key={i} value={t.text}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea
            data-testid="input-tagline"
            rows={4}
            className="fb-mt"
            value={quote.tagline}
            onChange={(e) => set("tagline", e.target.value)}
          />
        </Field>
      </Section>

      {/* 2. Cliente */}
      <Section n="2" title="Cliente (Spett.le)">
        <div className="fb-client-actions">
          <Button
            variant="outline"
            className="fb-client-btn"
            data-testid="btn-pick-client"
            onClick={onPickClient}
          >
            <Users size={13} /> Scegli da rubrica
          </Button>
          <Button
            variant="outline"
            className="fb-client-btn"
            data-testid="btn-save-client"
            onClick={onSaveClient}
          >
            <UserPlus size={13} /> Salva in rubrica
          </Button>
        </div>
        <Field label="Ragione sociale / Nome">
          <Input
            data-testid="input-client-name"
            value={quote.client.name}
            onChange={(e) => setClient("name", e.target.value)}
          />
        </Field>
        <div className="fb-grid-2">
          <Field label="P.IVA">
            <Input
              data-testid="input-client-vat"
              value={quote.client.vat || ""}
              onChange={(e) => setClient("vat", e.target.value)}
            />
          </Field>
          <Field label="Codice Fiscale">
            <Input
              value={quote.client.taxCode || ""}
              onChange={(e) => setClient("taxCode", e.target.value)}
            />
          </Field>
        </div>
        <Field label="Indirizzo">
          <Input
            data-testid="input-client-address"
            value={quote.client.address}
            onChange={(e) => setClient("address", e.target.value)}
          />
        </Field>
        <div className="fb-grid-3">
          <Field label="CAP">
            <Input
              data-testid="input-client-cap"
              value={quote.client.cap}
              onChange={(e) => setClient("cap", e.target.value)}
            />
          </Field>
          <Field label="Città">
            <Input
              data-testid="input-client-city"
              value={quote.client.city}
              onChange={(e) => setClient("city", e.target.value)}
            />
          </Field>
          <Field label="Prov.">
            <Input
              data-testid="input-client-province"
              value={quote.client.province}
              onChange={(e) => setClient("province", e.target.value)}
            />
          </Field>
        </div>
      </Section>

      {/* 3. Voci preventivo */}
      <Section n="3" title="Voci preventivo">
        <div className="fb-add-row">
          <Select onValueChange={addFromCatalog} value="">
            <SelectTrigger data-testid="select-catalog">
              <SelectValue placeholder="+ Aggiungi voce dal catalogo" />
            </SelectTrigger>
            <SelectContent className="fb-catalog-content">
              {Object.entries(groupedCatalog).map(([category, entries]) => (
                <SelectGroup key={category}>
                  <SelectLabel className="fb-cat-label">
                    <span
                      className="fb-cat-dot"
                      style={{ background: categoryDot(category) }}
                    />
                    {category}
                  </SelectLabel>
                  {entries.map((it) => (
                    <SelectItem key={it.id} value={it.id}>
                      {it.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            className="fb-free-btn"
            data-testid="btn-free-item"
            onClick={() =>
              items.add({
                category: "Personalizzata",
                name: "",
                description: "",
              })
            }
          >
            <Plus size={14} /> Voce libera
          </Button>
        </div>

        <div className="fb-items">
          {(quote.items || []).length === 0 && (
            <div className="fb-empty">Nessuna voce. Aggiungine una sopra.</div>
          )}
          {(quote.items || []).map((it, idx) => (
            <div
              className={`fb-item-card ${dragOver === idx ? "drag-over" : ""}`}
              key={it.id}
              data-testid="item-card"
              onDragOver={(e) => {
                e.preventDefault();
                if (dragOver !== idx) setDragOver(idx);
              }}
              onDragLeave={() => {
                if (dragOver === idx) setDragOver(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (dragIndex.current != null)
                  items.move(dragIndex.current, idx);
                dragIndex.current = null;
                setDragOver(null);
              }}
            >
              <div className="fb-item-cathead">
                <span
                  className="fb-item-drag"
                  data-testid="item-drag-handle"
                  draggable
                  onDragStart={(e) => {
                    dragIndex.current = idx;
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onDragEnd={() => {
                    dragIndex.current = null;
                    setDragOver(null);
                  }}
                  title="Trascina per riordinare"
                >
                  <GripVertical size={15} />
                </span>
                <Input
                  className="fb-item-cat"
                  data-testid="item-category"
                  value={it.category}
                  placeholder="Categoria"
                  onChange={(e) =>
                    items.update(it.id, "category", e.target.value)
                  }
                />
                <button
                  type="button"
                  className={`fb-item-eye ${it.showPrice ? "on" : ""}`}
                  data-testid="item-show-price"
                  title={
                    it.showPrice
                      ? "Prezzo visibile nel preventivo"
                      : "Prezzo nascosto nel preventivo"
                  }
                  onClick={() =>
                    items.update(it.id, "showPrice", !it.showPrice)
                  }
                >
                  {it.showPrice ? <Eye size={15} /> : <EyeOff size={15} />}
                </button>
                <button
                  type="button"
                  className="fb-item-del"
                  data-testid="item-delete"
                  onClick={() => items.remove(it.id)}
                >
                  <Trash2 size={15} />
                </button>
              </div>
              <Input
                className="fb-mt fb-item-name"
                data-testid="item-name"
                value={it.name}
                placeholder="Nome voce"
                onChange={(e) => items.update(it.id, "name", e.target.value)}
              />
              <Textarea
                className="fb-mt"
                data-testid="item-description"
                rows={3}
                value={it.description}
                placeholder="Descrizione"
                onChange={(e) => {
                  items.update(it.id, "description", e.target.value);
                  // Manual edits change character offsets: discard inherited
                  // Google Sheets rich-text metadata rather than misapplying it.
                  if (it.descriptionRich?.length)
                    items.update(it.id, "descriptionRich", []);
                }}
              />
              {!it.catalogId && it.name?.trim() && (
                <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => saveFreeItem(it)}
                    title="Aggiunge questa voce al foglio VOCI. Il preventivo può essere salvato anche senza farlo."
                  >
                    <Plus size={13} /> Salva nel catalogo
                  </Button>
                </div>
              )}
              <div className="fb-item-nums">
                <Field label="Qtà">
                  <Input
                    type="number"
                    min="0"
                    data-testid="item-qty"
                    value={it.qty}
                    onChange={(e) => items.update(it.id, "qty", e.target.value)}
                  />
                </Field>
                <Field label={it.recurring ? "Attivazione €" : "Prezzo unit. €"}>
                  <Input
                    type="number"
                    min="0"
                    data-testid="item-price"
                    value={it.unitPrice}
                    onChange={(e) =>
                      items.update(it.id, "unitPrice", e.target.value)
                    }
                  />
                </Field>
                <Field label="Sconto %">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    data-testid="item-discount"
                    value={it.discount}
                    onChange={(e) =>
                      items.update(it.id, "discount", e.target.value)
                    }
                  />
                </Field>
                <div className="fb-item-total">
                  <span className="fb-label">Totale riga</span>
                  <span data-testid="item-total">{eur(rowTotal(it))}</span>
                </div>
              </div>
              <label className="fb-item-recurring" data-testid="item-recurring-wrap">
                <Checkbox
                  data-testid="item-recurring"
                  checked={!!it.recurring}
                  onCheckedChange={(v) =>
                    items.update(it.id, "recurring", !!v)
                  }
                />
                <span>Servizio in abbonamento (canone mensile separato)</span>
              </label>
              {it.recurring && (
                <div className="fb-monthly">
                  <div className="fb-monthly-title">Abbonamento mensile</div>
                  <div className="fb-monthly-grid">
                    <Field label="Q.tà">
                      <Input
                        type="number"
                        min="0"
                        data-testid="item-monthly-qty"
                        value={it.monthlyQty}
                        onChange={(e) =>
                          items.update(it.id, "monthlyQty", e.target.value)
                        }
                      />
                    </Field>
                    <Field label="Canone €/mese">
                      <Input
                        type="number"
                        min="0"
                        data-testid="item-monthly-price"
                        value={it.monthlyPrice}
                        onChange={(e) =>
                          items.update(it.id, "monthlyPrice", e.target.value)
                        }
                      />
                    </Field>
                    <div className="fb-item-total">
                      <span className="fb-label">Totale /mese</span>
                      <span data-testid="item-monthly-total">
                        {eur(monthlyRowTotal(it))}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </Section>

      {/* 4. Prezzi e condizioni */}
      <Section n="4" title="Prezzi e condizioni">
        <Field label="Modalità prezzo">
          <div className="fb-modes" data-testid="price-modes">
            {PRICE_MODES.map((m) => (
              <button
                key={m.key}
                type="button"
                data-testid={`mode-${m.key}`}
                className={`fb-mode ${quote.priceMode === m.key ? "active" : ""}`}
                onClick={() => set("priceMode", m.key)}
              >
                {m.label}
              </button>
            ))}
          </div>
        </Field>

        <div className="fb-grid-2">
          <Field label="Sconto globale %">
            <Input
              type="number"
              min="0"
              max="100"
              data-testid="input-global-discount"
              value={quote.globalDiscount}
              onChange={(e) => set("globalDiscount", e.target.value)}
            />
          </Field>
          <div className="fb-check">
            <Checkbox
              id="reserved"
              data-testid="check-reserved"
              checked={quote.reservedPrice}
              onCheckedChange={(v) => set("reservedPrice", !!v)}
            />
            <Label htmlFor="reserved" className="fb-check-label">
              Mostra prezzo riservato
            </Label>
          </div>
        </div>

        <div className="fb-check">
          <Checkbox
            id="show-item-prices"
            data-testid="check-show-item-prices"
            checked={quote.showItemPrices}
            onCheckedChange={(v) => {
              set("showItemPrices", !!v);
              items.setAllShowPrice(!!v);
            }}
          />
          <Label htmlFor="show-item-prices" className="fb-check-label">
            Mostra prezzi per voce (attiva/disattiva tutte)
          </Label>
        </div>

        {/* Totale finale: arrotondamento / manuale */}
        <Field label="Totale finale — arrotonda o imposta manualmente">
          <div className="fb-round-row">
            <Input
              type="number"
              placeholder={`Auto: ${totals.computedFinal.toFixed(2)}`}
              data-testid="input-round-total"
              value={quote.roundTotal}
              onChange={(e) => set("roundTotal", e.target.value)}
            />
            <button
              type="button"
              className="fb-round-btn"
              data-testid="btn-round-100"
              onClick={() => roundTo(100)}
              title="Arrotonda ai 100"
            >
              ~100
            </button>
            <button
              type="button"
              className="fb-round-btn"
              data-testid="btn-round-500"
              onClick={() => roundTo(500)}
              title="Arrotonda ai 500"
            >
              ~500
            </button>
            <button
              type="button"
              className="fb-round-btn fb-round-clear"
              data-testid="btn-round-clear"
              onClick={() => set("roundTotal", "")}
              title="Ripristina automatico"
            >
              Auto
            </button>
          </div>
        </Field>

        {/* Riepilogo */}
        <div className="fb-summary" data-testid="summary">
          <div className="fb-sum-row">
            <span>Subtotale</span>
            <span>{eur(totals.subtotal)}</span>
          </div>
          {totals.globalDiscount > 0 && (
            <>
              <div className="fb-sum-row">
                <span>Sconto {totals.globalDiscount}%</span>
                <span>− {eur(totals.subtotal - totals.discounted)}</span>
              </div>
              <div className="fb-sum-row">
                <span>Netto scontato</span>
                <span>{eur(totals.discounted)}</span>
              </div>
            </>
          )}
          {totals.hasOverride && (
            <div className="fb-sum-row">
              <span>Totale impostato</span>
              <span>{eur(totals.final)}</span>
            </div>
          )}
          <div className="fb-sum-row fb-sum-total">
            <span>Totale</span>
            <span data-testid="summary-total">
              {eur(totals.final)}
              {suffix}
            </span>
          </div>
        </div>

        <div className="fb-grid-2">
          <Field label="Offerta valida fino al">
            <Input
              type="date"
              data-testid="input-valid-until"
              value={quote.validUntil}
              onChange={(e) => set("validUntil", e.target.value)}
            />
          </Field>
          <Field label="Tempi di elaborazione">
            <Input
              data-testid="input-processing"
              placeholder="60 giorni dall'accettazione"
              value={quote.processingTime}
              onChange={(e) => set("processingTime", e.target.value)}
            />
          </Field>
        </div>
        <Field label="Termini di pagamento">
          <Input
            data-testid="input-payment"
            placeholder="30% all'accettazione, saldo alla consegna"
            value={quote.paymentTerms}
            onChange={(e) => set("paymentTerms", e.target.value)}
          />
        </Field>
        <Field label="Note / condizioni aggiuntive">
          <Textarea
            rows={3}
            data-testid="input-notes"
            value={quote.notes}
            onChange={(e) => set("notes", e.target.value)}
          />
        </Field>
      </Section>
    </div>
  );
};
